// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useRef } from 'react';

// react-router components
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { RequireAuth, useAuth } from 'auth';
import { useReactiveVar } from '@apollo/client/react';
import Clarity from '@microsoft/clarity';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox';
import { AdminCookieNotice } from 'components/admin-cookie-notice';
import { SuiSnackbarProvider } from 'components/SuiSnackbar';

// Soft UI Dashboard PRO React example components
import Sidenav from 'examples/Sidenav';

// Soft UI Dashboard PRO React themes
import theme from 'assets/theme';
import themeRTL from 'assets/theme/theme-rtl';
import { activeApolloRequestsVar } from 'apollo/network-status';

// RTL plugins
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'lib/stylis-rtl';

// Soft UI Dashboard PRO React routes
import routes from 'routes';

// Soft UI Dashboard PRO React contexts
import { useSoftUIController, setMiniSidenav } from 'context';

// Images
import brand from 'assets/images/logo-ct.svg';
import { persistPreferredLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { supportedLocales } from 'lib/i18n/config';
import { initializeAdminMarketingAnalytics, trackAdminPageView } from 'lib/marketing-analytics';
import { stripLocalePrefix, withLocale } from 'lib/i18n/path';
import { useReviewModerationAccess } from 'review';
import { SiteAssistantRequestPrompt, useSiteEntitlements } from 'site';

const LOCAL_SIDENAV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);
const NON_LOCAL_ALLOWED_SIDENAV_KEYS = new Set([
  'dashboard',
  'events',
  'proposals',
  'messages',
  'services',
  'galleries',
  'reviews',
  'site',
]);
// Shown only where the Account's plan includes a site, like "Мій сайт" in the app's Profile menu.
const SITE_SIDENAV_KEY = 'site';
const HIDDEN_SIDENAV_KEYS = new Set(['positions']);
const SIDENAV_PRIORITY_KEYS = new Map([['dashboard', 0]]);
// Shown only to the Accounts the API lets moderate Reviews.
const REVIEW_MODERATION_SIDENAV_KEY = 'reviews';

function isPublicRoute(routePath) {
  return routePath === '/sign-in' || routePath === '/sign-up';
}

function getRouteVariants(routePath) {
  return [routePath, ...supportedLocales.map((locale) => withLocale(routePath, locale))];
}

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const clarityInitializedRef = useRef(false);
  const { pathname, search } = useLocation();
  const { accessToken, isAuthenticated, isInitializing } = useAuth();
  const reviewModeration = useReviewModerationAccess(isAuthenticated, accessToken);
  const activeApolloRequests = useReactiveVar(activeApolloRequestsVar);
  const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID?.trim();
  const locale = useLocale();
  const normalizedPathname = stripLocalePrefix(pathname).pathname;
  const { isSiteAvailable } = useSiteEntitlements();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  useEffect(() => {
    persistPreferredLocale(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    initializeAdminMarketingAnalytics();
  }, []);

  useEffect(() => {
    if (!clarityProjectId || typeof window === 'undefined' || clarityInitializedRef.current) {
      return;
    }

    Clarity.init(clarityProjectId);
    clarityInitializedRef.current = true;
  }, [clarityProjectId]);

  useEffect(() => {
    trackAdminPageView(pathname, search);
  }, [pathname, search]);

  if (isInitializing) {
    const loadingTheme = direction === 'rtl' ? themeRTL : theme;

    return (
      <ThemeProvider theme={loadingTheme}>
        <CssBaseline />
        <SoftBox
          minHeight="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ backgroundColor: ({ palette }) => palette.background.default }}
        >
          <CircularProgress color="info" />
        </SoftBox>
      </ThemeProvider>
    );
  }

  const isOnPublicRoute = isPublicRoute(normalizedPathname);
  const shouldRenderDashboardChrome = isAuthenticated && !isOnPublicRoute && layout === 'dashboard';
  const isLocalBuild =
    process.env.NODE_ENV === 'development' ||
    LOCAL_SIDENAV_HOSTNAMES.has(window.location.hostname) ||
    window.location.hostname.endsWith('.localhost');
  const sidenavRoutes = isLocalBuild
    ? routes
    : routes.filter(
        ({ type, key }) => type === 'collapse' && NON_LOCAL_ALLOWED_SIDENAV_KEYS.has(key)
      );
  const visibleSidenavRoutes = [...sidenavRoutes]
    .filter((route) => !HIDDEN_SIDENAV_KEYS.has(route.key))
    .filter((route) => route.key !== SITE_SIDENAV_KEY || isSiteAvailable)
    .filter(
      (route) =>
        route.key !== REVIEW_MODERATION_SIDENAV_KEY || reviewModeration.access === 'moderator'
    )
    .map((route) =>
      route.key === REVIEW_MODERATION_SIDENAV_KEY && reviewModeration.openReportsCount
        ? { ...route, badge: reviewModeration.openReportsCount }
        : route
    )
    .sort((leftRoute, rightRoute) => {
      const leftPriority = SIDENAV_PRIORITY_KEYS.get(leftRoute.key) ?? Number.MAX_SAFE_INTEGER;
      const rightPriority = SIDENAV_PRIORITY_KEYS.get(rightRoute.key) ?? Number.MAX_SAFE_INTEGER;

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return 0;
    });

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const routeElement = isPublicRoute(route.route) ? (
          route.component
        ) : (
          <RequireAuth>{route.component}</RequireAuth>
        );

        return getRouteVariants(route.route).map((routePath) => (
          <Route path={routePath} element={routeElement} key={`${route.key}-${routePath}`} />
        ));
      }

      return null;
    });

  const appContent = (
    <Routes>
      {getRoutes(routes)}
      <Route
        path="/"
        element={
          <Navigate to={withLocale(isAuthenticated ? '/dashboard' : '/sign-in', locale)} replace />
        }
      />
      {supportedLocales.map((supportedLocale) => (
        <Route
          key={`root-${supportedLocale}`}
          path={`/${supportedLocale}`}
          element={
            <Navigate
              to={withLocale(isAuthenticated ? '/dashboard' : '/sign-in', supportedLocale)}
              replace
            />
          }
        />
      ))}
      <Route
        path="*"
        element={
          <Navigate to={withLocale(isAuthenticated ? '/dashboard' : '/sign-in', locale)} replace />
        }
      />
    </Routes>
  );

  const apolloProgress =
    activeApolloRequests > 0 ? (
      <SoftBox
        sx={({ palette, zIndex, functions: { pxToRem } }) => ({
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: zIndex.drawer + 2,
          pointerEvents: 'none',
          backgroundColor: 'transparent',
          boxShadow: `0 ${pxToRem(10)} ${pxToRem(24)} rgba(15, 23, 42, 0.12)`,
          '& .MuiLinearProgress-root': {
            height: pxToRem(4),
            borderRadius: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.28)',
          },
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${palette.info.main} 0%, ${palette.warning.main} 100%)`,
          },
        })}
      >
        <LinearProgress color="info" />
      </SoftBox>
    ) : null;

  return direction === 'rtl' ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {apolloProgress}
        {shouldRenderDashboardChrome && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brand}
              brandName="Matcha Events"
              routes={visibleSidenavRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        {appContent}
        {shouldRenderDashboardChrome && isSiteAvailable ? <SiteAssistantRequestPrompt /> : null}
        <AdminCookieNotice />
        <SuiSnackbarProvider />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {apolloProgress}
      {shouldRenderDashboardChrome && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Matcha Events"
            routes={visibleSidenavRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      {appContent}
      {shouldRenderDashboardChrome && isSiteAvailable ? <SiteAssistantRequestPrompt /> : null}
      <AdminCookieNotice />
      <SuiSnackbarProvider />
    </ThemeProvider>
  );
}
